/**
 * GDPR filter — agency-level per-MCP personal-data settings API client.
 *
 * Thin fetch wrapper matching the existing codebase convention (see
 * `src/lib/api/tool-access/tool-access-api.ts`). RTK Query intentionally NOT
 * used — consistent with the other Phase 1+ clients.
 *
 * Auth header: `Authorization: Bearer ${state.auth.agency.accessToken}` (Redux
 * only, never persisted). Base URL `CENTRAL_API_BASE_URL` already resolves to
 * `${env-base}/api/v1/`, so paths below omit the `/api/v1/` prefix.
 *
 * Backend contract (McpControlCenter — GdprSettingsController):
 *   GET    /api/v1/agency/gdpr-settings
 *   PUT    /api/v1/agency/gdpr-settings/{platform}
 *   DELETE /api/v1/agency/gdpr-settings/{platform}
 *
 * Settings are ORG-scoped (one GDPR stance for every brand of the agency).
 * Default when a platform was never customised: filter ENABLED with ALL
 * categories blocked — the gateway enforces the same default and fails closed
 * when the config is unreachable.
 */
import { CENTRAL_API_BASE_URL } from '../../constants/central-api';
import store from '../../../store/store';

// ---------- shared types ----------

/**
 * The canonical category vocabulary. Kept in sync with central-api
 * `Support/GdprCategories.php` and the gateway `src/core/gdpr-filter.js`.
 * The backend's `categories` response is authoritative at runtime; this const
 * exists for typing and stable ordering.
 */
export const GDPR_CATEGORIES = [
  'email',
  'phone',
  'name',
  'address',
  'ip_address',
  'id_numbers',
  'user_ids',
  'dob',
  'location',
  'financial',
  'message_content',
  'audience_data',
] as const;

export type GdprCategoryKey = (typeof GDPR_CATEGORIES)[number];

/** A {key,label} category row as served by the backend catalog. */
export interface GdprCategory {
  key: string;
  label: string;
}

/** One MCP platform's effective GDPR setting for the agency. */
export interface GdprPlatformSetting {
  platform_key: string;
  label: string;
  /** True when at least one brand of the org has a working connection to this MCP. */
  connected: boolean;
  /** Number of the org's brands with a working connection (absent on older APIs). */
  connected_brands?: number;
  /** Master switch — false = no filtering at all for this MCP server. */
  enabled: boolean;
  blocked_categories: string[];
  /** False = pure default (no explicit row stored for this org+platform). */
  is_customised: boolean;
  updated_at?: string | null;
}

/** Response payload of GET /agency/gdpr-settings. */
export interface GdprSettingsResponse {
  categories: GdprCategory[];
  default: { enabled: boolean; blocked_categories: string[] };
  platforms: GdprPlatformSetting[];
}

/** Body for PUT /agency/gdpr-settings/{platform}. */
export interface UpdateGdprSettingRequest {
  enabled: boolean;
  blocked_categories: string[];
}

/** Shape returned by the PUT/DELETE endpoints (single-platform echo). */
export interface GdprSettingEcho {
  platform_key: string;
  enabled: boolean;
  blocked_categories: string[];
  is_customised: boolean;
  updated_at?: string | null;
}

// ---------- internal helpers ----------

function getAuthHeader(token?: string): Record<string, string> {
  const t = token ?? store.getState().auth.agency.accessToken;
  const headers: Record<string, string> = {
    Accept: 'application/json',
  };
  if (t) {
    headers['Authorization'] = `Bearer ${t}`;
  }
  return headers;
}

async function parseJson<T>(response: Response): Promise<T> {
  const text = await response.text();
  if (!text) {
    return {} as T;
  }
  try {
    return JSON.parse(text) as T;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error('[gdpr-api] failed to parse JSON', err, text);
    throw new Error('Invalid response from server.');
  }
}

async function request<T>(
  path: string,
  init: RequestInit = {},
  tokenOverride?: string
): Promise<T> {
  const headers: Record<string, string> = {
    ...getAuthHeader(tokenOverride),
    ...((init.headers as Record<string, string>) || {}),
  };

  if (init.body && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetch(`${CENTRAL_API_BASE_URL}${path}`, {
    ...init,
    headers,
  });

  if (!response.ok) {
    let detail = '';
    try {
      const body = await parseJson<{ message?: string; error?: string }>(
        response
      );
      detail = body?.message || body?.error || '';
    } catch {
      // swallow — fall back to status text
    }
    const err = new Error(
      detail || `Request failed (${response.status} ${response.statusText})`
    ) as Error & { status?: number };
    err.status = response.status;
    throw err;
  }

  return parseJson<T>(response);
}

/** Unwrap a `{ data }` envelope when present; pass through a bare payload. */
function unwrap<T>(raw: T | { data?: T }): T {
  if (
    raw &&
    typeof raw === 'object' &&
    'data' in (raw as Record<string, unknown>) &&
    (raw as { data?: T }).data !== undefined
  ) {
    return (raw as { data: T }).data;
  }
  return raw as T;
}

// ---------- public API ----------

/**
 * GET /api/v1/agency/gdpr-settings — the category catalog + every known MCP
 * platform's effective setting for the caller's agency.
 */
export async function getGdprSettings(
  token?: string
): Promise<GdprSettingsResponse> {
  const raw = await request<GdprSettingsResponse | { data?: GdprSettingsResponse }>(
    'agency/gdpr-settings',
    { method: 'GET' },
    token
  );
  const res = unwrap(raw);
  return {
    categories: Array.isArray(res?.categories) ? res.categories : [],
    default: res?.default ?? { enabled: true, blocked_categories: [] },
    platforms: Array.isArray(res?.platforms) ? res.platforms : [],
  };
}

/**
 * PUT /api/v1/agency/gdpr-settings/{platform} — upsert one MCP platform's
 * setting (master switch + blocked category list). Owner/manager only.
 */
export async function updateGdprSetting(
  platformKey: string,
  body: UpdateGdprSettingRequest,
  token?: string
): Promise<GdprSettingEcho> {
  const raw = await request<GdprSettingEcho | { data?: GdprSettingEcho }>(
    `agency/gdpr-settings/${encodeURIComponent(platformKey)}`,
    { method: 'PUT', body: JSON.stringify(body) },
    token
  );
  return unwrap(raw);
}

/**
 * DELETE /api/v1/agency/gdpr-settings/{platform} — remove the explicit
 * setting so the platform falls back to the all-blocked default.
 */
export async function resetGdprSetting(
  platformKey: string,
  token?: string
): Promise<GdprSettingEcho> {
  const raw = await request<GdprSettingEcho | { data?: GdprSettingEcho }>(
    `agency/gdpr-settings/${encodeURIComponent(platformKey)}`,
    { method: 'DELETE' },
    token
  );
  return unwrap(raw);
}
